/**
 * The sentence a page says when a vault is reported by more than one
 * computer — the portal's Overview and job-detail pages, both grouping
 * `Repository` rows by `repoId` (`#308`, `#309`).
 *
 * "Computer" rather than "machine", which is what the rest of the portal
 * calls the same thing: this sentence borrows the desktop's word on purpose,
 * because it says the same thing the desktop's delete warning already tells
 * the person at the keyboard (`apps/desktop/CONTEXT.md`'s Computer entry), and
 * one fact should not have two names depending which screen reports it.
 */

import { joinWithAnd } from "./format";

/**
 * `null` for a vault nobody else reports — the ordinary case, which gets no
 * sentence at all rather than "Shared by 1 computer" — and otherwise the
 * names of every computer known to report it, in the order given.
 */
export function sharedByComputersSentence(computerNames: readonly string[]): string | null {
  if (computerNames.length < 2) return null;

  return `Shared by ${computerNames.length} computers: ${joinWithAnd(computerNames)}.`;
}

/**
 * Repository rows grouped one group per vault, in the order each vault's
 * first row arrives: rows that report the same `repoId` are one vault, and a
 * row that reports none is a vault of its own (`#308`). The one grouping the
 * portal's vault list, the summary email and the overview's vault checks all
 * use, so they never disagree about which rows are one vault.
 */
export function groupByVault<Row extends { readonly id: string; readonly repoId?: string | null }>(
  rows: readonly Row[],
): Row[][] {
  const groups = new Map<string, Row[]>();
  for (const row of rows) {
    // A `repoId` is 32 hex characters, so it can never collide with a key
    // that starts `row:`.
    const key = row.repoId ?? `row:${row.id}`;
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  }
  return [...groups.values()];
}

/**
 * The names of every machine with a `Repository` row reporting `repoId`, one
 * name per row — the Overview and job-detail pages both need this list before
 * they can ask {@link sharedByComputersSentence} anything, and writing the
 * lookup twice is how the two pages would drift apart on what "shares a
 * vault" means.
 *
 * A machine the overview has no record of names itself by id rather than
 * disappearing from the list — the same fallback `nameOf` uses elsewhere —
 * because a row with nobody to blame it on is still a row that shares the
 * vault.
 */
export function machineNamesForRepo(
  repositories: readonly { readonly repoId?: string; readonly machineId: string }[],
  machines: readonly { readonly id: string; readonly name: string }[],
  repoId: string,
): string[] {
  return repositories
    .filter((r) => r.repoId === repoId)
    .map((r) => machines.find((m) => m.id === r.machineId)?.name ?? r.machineId);
}
